import {
	Button,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	Grid,
	Snackbar,
	TextField,
	Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { DateTimePicker } from "@mui/x-date-pickers";
import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import { TimeService, getMomentTz } from "../../../../../services";
import { Job } from "../../models/Job";
import { upsertJob } from "../../repositories/JobsRepository";

const moment = getMomentTz();

type Props = {
	job: Job | null;
	modal: "create" | "update" | "reset" | "enable" | "";
	onHide: ({ close, entityId }: { close: boolean; entityId?: number }) => void;
};

export const JobDetails = ({ job, modal, onHide }: Props) => {
	const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);
	const [error, setError] = useState("");
	const [openSnackbar, setOpenSnackbar] = useState(false);

	const succesfullyMessage = `The job was ${modal}d succesfully`;

	const {
		control,
		reset,
		handleSubmit,
		formState: { dirtyFields },
	} = useForm<Job>({
		mode: "onChange",
	});

	const isFormDirty = !(Object.keys(dirtyFields).length === 0);

	const onSubmit: SubmitHandler<Job> = async (data: Job) => {
		setIsSubmitLoading(true);
		if (modal !== "reset") {
			const resp = await upsertJob(data);
			if (resp.ok) {
				setOpenSnackbar(true);
				onHide({ close: false, entityId: resp.data.returnValue });
			} else {
				setError(resp.message);
			}
		}
		setIsSubmitLoading(false);
	};

	useEffect(() => {
		if (modal === "create" || modal === "update") {
			reset(
				job || {
					sProcedure: "",
					RestartAfterFailure: true,
					PeriodTime: null,
					StartTime: null,
					LastStatus: null,
					LastExecutionTime: null,
					LastExecutionResult: "",
					NextExecutionTime: null,
				},
			);
			setIsSubmitLoading(false);
		}
	}, [modal, job]);

	return (
		<>
			{(job === null || modal !== "update") && modal !== "create" ? (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						height: "100%",
						width: "100%",
					}}
				>
					<Typography>Select a job or create a new one</Typography>
				</div>
			) : (
				<form onSubmit={handleSubmit(onSubmit)}>
					<Grid container spacing={1}>
						<Grid item xs={12} md={12}>
							<Controller
								name="sProcedure"
								control={control}
								rules={{ required: "Name is required" }}
								render={({ field, fieldState: { error } }) => (
									<TextField
										label="Name"
										value={field.value ?? ""}
										onChange={field.onChange}
										variant="outlined"
										size="small"
										fullWidth
										error={!!error}
										helperText={error?.message}
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<Controller
								name="RestartAfterFailure"
								control={control}
								render={({ field }) => (
									<FormControlLabel
										control={
											<Checkbox
												checked={!!field.value}
												onChange={field.onChange}
												name="RestartAfterFailure"
												color="primary"
											/>
										}
										label="Restart After Failure"
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} md={6} style={{ marginTop: -7 }}>
							<Controller
								name="PeriodTime"
								control={control}
								rules={{ required: "Period time is required" }}
								render={({ field, fieldState: { error } }) => (
									<TextField
										label="Period Time"
										value={field.value ?? ""}
										onChange={field.onChange}
										variant="outlined"
										size="small"
										margin="dense"
										fullWidth
										error={!!error}
										helperText={error?.message}
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<Controller
								name="StartTime"
								control={control}
								render={({ field, fieldState: { error } }) => (
									<>
										<DateTimePicker
											label="Start Time"
											value={field.value ? moment(field.value) : null}
											onChange={field.onChange}
											format="MM/DD/YYYY HH:mm:ss"
											views={["year", "month", "day", "hours", "minutes"]}
											ampm={false}
											timezone={TimeService.getInstance().getServerTimeZone()}
											slotProps={{
												textField: {
													size: "small",
													margin: "none",
													error: !!error,
												},
											}}
										/>
									</>
								)}
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<Controller
								name="LastStatus"
								control={control}
								render={({ field }) => (
									<TextField
										label="Last Status"
										value={field.value ?? ""}
										variant="outlined"
										size="small"
										fullWidth
										disabled
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<Controller
								name="LastExecutionTime"
								control={control}
								render={({ field }) => (
									<DateTimePicker
										label="Last Execution Time"
										value={field.value ? moment(field.value) : null}
										format="MM/DD/YYYY HH:mm:ss"
										views={["year", "month", "day", "hours", "minutes"]}
										ampm={false}
										timezone={TimeService.getInstance().getServerTimeZone()}
										disabled
										slotProps={{
											textField: {
												size: "small",
												margin: "none",
											},
										}}
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<Controller
								name="NextExecutionTime"
								control={control}
								render={({ field }) => (
									<DateTimePicker
										label="Next Execution Time"
										value={field.value ? moment(field.value) : null}
										format="MM/DD/YYYY HH:mm:ss"
										views={["year", "month", "day", "hours", "minutes"]}
										ampm={false}
										timezone={TimeService.getInstance().getServerTimeZone()}
										disabled
										slotProps={{
											textField: {
												size: "small",
												margin: "none",
											},
										}}
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} md={12}>
							<Controller
								name="LastExecutionResult"
								control={control}
								render={({ field }) => (
									<TextField
										label="Last Execution Result"
										value={field.value ?? ""}
										variant="outlined"
										size="small"
										margin="dense"
										fullWidth
										multiline
										minRows={2}
										maxRows={2}
										disabled
									/>
								)}
							/>
						</Grid>
						<Grid
							container
							spacing={2}
							justifyContent="flex-end"
							style={{ margin: 15 }}
						>
							<Grid container spacing={2} justifyContent="flex-end">
								<Grid item md={3} xs={12} style={{ margin: 0 }}>
									<Button
										fullWidth
										variant="contained"
										color="default"
										onClick={() => onHide({ close: true })}
									>
										Close
									</Button>
								</Grid>
								<Grid item md={3} xs={12} style={{ margin: 0 }}>
									<Button
										fullWidth
										startIcon={
											isSubmitLoading && <CircularProgress size="1rem" />
										}
										disabled={isSubmitLoading || !isFormDirty}
										variant="contained"
										color="primary"
										type="submit"
									>
										Save
									</Button>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</form>
			)}
			<Snackbar
				open={openSnackbar}
				autoHideDuration={2500}
				onClose={() => setOpenSnackbar(false)}
			>
				<Alert severity="success" onClose={() => setOpenSnackbar(false)}>
					The job was updated succesfully
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
